import { useRef, useState, type ReactNode } from 'react';
import { Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Icon } from '@ui/Icon';
import { getTooltipBaseStyles } from '@client/style/tooltip.styles';

const useStyles = makeStyles()((theme) => ({
  root: { position: 'relative', maxWidth: '100%', minWidth: 0 },
  editing: { width: 'max-content' },
  mirror: {
    ...getTooltipBaseStyles(theme),
    visibility: 'hidden',
    boxSizing: 'border-box',
    minWidth: 'calc(1ch + 24px)',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    lineHeight: 'inherit',
  },
  edit: {
    position: 'absolute',
    right: '100%',
    top: '50%',
    transform: 'translateY(-50%)',
    marginRight: 6,
    border: 0,
    padding: 2,
    background: 'none',
    cursor: 'pointer',
    color: 'rgba(0,0,0,0.5)',
    opacity: 0,
    '[data-history-comment-row]:hover &': { opacity: 1 },
    '&:focus-visible': { opacity: 1 },
    '&:hover': { color: 'rgba(0,0,0,1)' },
  },
  input: {
    ...getTooltipBaseStyles(theme),
    cursor: 'text',
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    resize: 'none',
    margin: 0,
    borderRadius: 12,
    boxShadow: 'none',
    border: 0,
    outline: `1px solid ${theme.palette.custom.focus}`,
    backgroundColor: '#fff',
    color: theme.palette.text.primary,
    fontFamily: 'inherit',
    lineHeight: 'inherit',
    overflowWrap: 'anywhere',
    overflow: 'hidden',
  },
  error: { color: theme.palette.error.main, fontSize: 11, margin: 0 },
}));

type EditableHistoryCommentProps = {
  message: string;
  editable: boolean;
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  onSave: (message: string) => Promise<void>;
  onDelete: () => Promise<void>;
  children: ReactNode;
};

/** The hidden text mirror sizes the editor to the current draft. */
export const EditableHistoryComment = ({
  message,
  editable,
  editing,
  onEditingChange: setEditing,
  onSave,
  onDelete,
  children,
}: EditableHistoryCommentProps) => {
  const { classes, cx } = useStyles();
  const [draft, setDraft] = useState(message);
  const [error, setError] = useState<string>();
  const saving = useRef(false);
  const hasDraft = useRef(false);
  const originalMessage = useRef(message);
  const startEditing = () => {
    if (!hasDraft.current) {
      originalMessage.current = message;
      setDraft(message);
    }
    setError(undefined);
    setEditing(true);
  };
  const submit = async () => {
    if (saving.current || !draft.trim()) return;
    if (!error && draft === originalMessage.current) {
      hasDraft.current = false;
      setEditing(false);
      return;
    }
    if (message !== originalMessage.current) {
      setError('Comment changed. Cancel and reopen to edit the latest text.');
      return;
    }
    saving.current = true;
    setError(undefined);
    try {
      await onSave(draft);
      hasDraft.current = false;
      setEditing(false);
    } catch {
      setError('Could not save comment. Try again.');
    } finally {
      saving.current = false;
    }
  };
  const remove = async () => {
    if (saving.current) return;
    saving.current = true;
    setError(undefined);
    try {
      await onDelete();
    } catch {
      setError('Could not delete comment. Try again.');
    } finally {
      saving.current = false;
    }
  };
  return (
    <div
      data-history-comment-row=""
      className={cx(classes.root, editing && classes.editing)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setEditing(false);
        }
      }}
      onDoubleClick={(event) => {
        if (!editable || editing) return;
        if (
          event.target instanceof Element &&
          event.target.closest('button, a, input, textarea')
        )
          return;
        event.preventDefault();
        startEditing();
      }}
    >
      {editing ? (
        <div aria-hidden="true" className={classes.mirror}>
          {draft + '\u200b'}
        </div>
      ) : (
        children
      )}
      {editable && (
        <Tooltip title={editing ? 'Delete' : 'Edit comment'} placement="left">
          <button
            type="button"
            aria-label={editing ? 'Delete' : 'Edit comment'}
            className={classes.edit}
            onClick={() => {
              if (editing) {
                void remove();
                return;
              }
              startEditing();
            }}
          >
            <Icon category="mui" icon={editing ? 'Delete' : 'Edit'} size={14} />
          </button>
        </Tooltip>
      )}
      {editing && (
        <textarea
          aria-label="Edit comment"
          className={classes.input}
          value={draft}
          autoFocus
          onChange={(event) => {
            setDraft(event.target.value);
            hasDraft.current = event.target.value !== originalMessage.current;
            setError(undefined);
          }}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.nativeEvent.isComposing) return;
            if (event.key === 'Escape') {
              event.preventDefault();
              hasDraft.current = false;
              setEditing(false);
            }
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void submit();
            }
          }}
        />
      )}
      {error && (
        <p role="alert" className={classes.error}>
          {error}
        </p>
      )}
    </div>
  );
};
